import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import startOfDay from 'date-fns/startOfDay';

import FormGroup from '../../../../../../../components/global/FormGroup';
import Alert from '../../../../../../../components/global/Alert';
import { getFormikTouchedErrors } from '../../../../../../../util/formik';
import Label from '../../../../../../../components/global/Label';
import InputGroup from '../../../../../../../components/global/InputGroup';
import { getAsset } from '../../../../../../../util/assets';
import { EVENT_DETAILS_CONTEXT } from '../utils';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    anniversary_type: PropTypes.string,
    event_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  context: PropTypes.oneOf(Object.values(EVENT_DETAILS_CONTEXT)),
};

const GraduationEventDetails = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  context,
}) => {
  const dateInputRef = useRef();
  const graduationDateInputRef = useRef();
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));
  const { date, graduation_date: graduationDate } = values;

  useEffect(() => {
    if (dateInputRef?.current) {
      dateInputRef.current.readOnly = 'readonly';
    }
  }, [dateInputRef]);

  useEffect(() => {
    if (graduationDateInputRef?.current) {
      graduationDateInputRef.current.readOnly = 'readonly';
    }
  }, [graduationDateInputRef]);

  return (
    <Fragment>
      <FormGroup
        id="name"
        name="name"
        error={!!errors.name}
        label={
          context === EVENT_DETAILS_CONTEXT.SLIDESHOW
            ? 'Slideshow Name'
            : 'Event Name'
        }
        placeholder="Enter event name here..."
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.name}
      />
      <div className="row">
        <div className="col-12">
          <div className="row">
            <FormGroup
              id="first_name"
              name="first_name"
              error={!!errors.first_name}
              label="Name of the celebrated"
              placeholder="First name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.first_name}
              className="col-12 col-lg-6 mb-1"
            />
            <FormGroup
              id="last_name"
              name="last_name"
              error={!!errors.last_name}
              label={<>&nbsp;</>}
              labelProps={{
                className: 'd-none d-lg-block',
              }}
              className="col-12 col-lg-6"
              placeholder="Last name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.last_name}
            />
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="form-group">
            <Label id="date" optional>
              Graduation date
            </Label>
            <div className="datepicker-gray-light">
              <DatePicker
                id="graduation_date"
                name="graduation_date"
                customInput={
                  <InputGroup
                    id="graduation_date"
                    name="graduation_date"
                    error={!!errors.graduation_date}
                    type="text"
                    autoComplete="off"
                    inputRef={graduationDateInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'graduation_date',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={graduationDate}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="form-group">
            <Label id="date" optional>
              Having a Party?
            </Label>
            <div className="datepicker-gray-light">
              <DatePicker
                id="date"
                name="date"
                customInput={
                  <InputGroup
                    id="date"
                    name="date"
                    error={!!errors.date}
                    type="text"
                    autoComplete="off"
                    inputRef={dateInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={date}
              />
            </div>
          </div>
        </div>
      </div>

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

GraduationEventDetails.propTypes = propTypes;

export default GraduationEventDetails;
